// Suggestions for what to add to the tree next, shown on the To-dos page under the to-dos
// themselves: each can be done, turned into a to-do, or dismissed (kept in the tree, so it syncs).

import {
  Baby,
  BookOpen,
  Briefcase,
  CalendarDays,
  CalendarHeart,
  Camera,
  Copy,
  Fingerprint,
  Heart,
  Home,
  Hourglass,
  Link2,
  ListPlus,
  MapPin,
  MoreHorizontal,
  Network,
  PenLine,
  Quote,
  RotateCcw,
  EyeOff,
  Sparkles,
  SpellCheck,
  TriangleAlert,
  UserPlus,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { formatDate } from '../../model/dates';
import { defaultPronouns, displayName, newId, withEventField } from '../../model/people';
import {
  CLOSENESS,
  CLOSENESS_FAR,
  CLOSENESS_NO_HOME,
  CLOSENESS_UNLINKED,
  DIRECT_LINE,
  ELDER,
  SUGGESTION_RULES,
  suggestions as suggestionsFor,
  VARIETY,
  type Suggestion,
  type SuggestionAction,
  type SuggestionKind,
} from '../../model/suggestions';
import { addRelationship, removeTodo, setDismissed, upsertPerson, upsertTodo, wouldCreateCycle } from '../../model/tree';
import type { Id, Relationship, Tree } from '../../model/types';
import { commit, getState, openDialog, setState, setView, toast, undo, updateSettings, useApp } from '../store';
import { Menu } from './Menu';
import { PersonLink } from './common';

const ICONS: Record<SuggestionKind, LucideIcon> = {
  home: Home,
  duplicate: Copy,
  dates: TriangleAlert,
  'link-parents': Link2,
  parents: UserPlus,
  'link-partners': Heart,
  unlinked: Network,
  name: PenLine,
  'birth-date': Baby,
  gender: Fingerprint,
  living: Hourglass,
  spelling: SpellCheck,
  'partnership-date': CalendarHeart,
  'death-date': CalendarDays,
  sources: BookOpen,
  photo: Camera,
  birthplace: MapPin,
  'cite-birth': Quote,
  occupation: Briefcase,
};

/** The to-do kind a suggestion becomes (from the kinds the to-do editor offers). */
const TODO_KINDS: Partial<Record<SuggestionKind, string>> = {
  duplicate: 'Verify',
  dates: 'Verify',
  spelling: 'Verify',
  'link-parents': 'Cleanup',
  'link-partners': 'Cleanup',
  unlinked: 'Cleanup',
  home: 'Cleanup',
  name: 'Ask a relative',
  gender: 'Ask a relative',
  'cite-birth': 'Order a record',
  'birth-date': 'Missing events',
  'death-date': 'Missing events',
  'partnership-date': 'Missing events',
  birthplace: 'Missing events',
  occupation: 'Missing events',
};

const PAGE = 25;
const NO_KINDS: string[] = [];

/** Dismiss suggestions in the open tree, or bring them back. */
function setDismissedIn(ids: string[], dismissed: boolean) {
  const t = getState().tree;
  if (t) commit(setDismissed(t, ids, dismissed));
}

/** Suggestions for the To-dos page, optionally only those about one person. */
export function SuggestionsSection({ person }: { person: Id | null }) {
  const tree = useApp((s) => s.tree)!;
  const all = useMemo(() => suggestionsFor(tree), [tree]);
  // Default outside the selector: a new [] on every read would make the store think it changed, forever.
  const hiddenKinds = (useApp((s) => s.settings.hiddenSuggestionKinds) ?? NO_KINDS) as SuggestionKind[];
  const [kind, setKind] = useState<SuggestionKind | null>(null);
  const [limit, setLimit] = useState(PAGE);
  useEffect(() => setLimit(PAGE), [kind, person, tree.id]);

  const hidden = new Set(Object.keys(tree.dismissed ?? {}));
  // Suggestions already on the to-do list are tracked there instead.
  const tracked = new Set(Object.values(tree.todos).map((t) => t.suggestionId).filter(Boolean));
  const about = (x: Suggestion) => !person || x.people.includes(person);
  const open = all.filter((x) => !hidden.has(x.id) && !tracked.has(x.id) && !hiddenKinds.includes(x.kind) && about(x));
  const gone = all.filter((x) => hidden.has(x.id) && !hiddenKinds.includes(x.kind) && about(x));
  const counts = new Map<SuggestionKind, number>();
  for (const x of open) counts.set(x.kind, (counts.get(x.kind) ?? 0) + 1);
  // A filter whose last suggestion was just dismissed falls back to everything.
  const active = kind && counts.get(kind) ? kind : null;
  const list = active ? open.filter((x) => x.kind === active) : open;
  const home = tree.homePersonId && tree.people[tree.homePersonId];

  const dismiss = (x: Suggestion) => {
    setDismissedIn([x.id], true);
    toast('Suggestion dismissed.', 'info', { label: 'Undo', run: () => setDismissedIn([x.id], false) });
  };
  const restore = (ids: string[]) => setDismissedIn(ids, false);
  const setHiddenKinds = (kinds: SuggestionKind[]) => updateSettings({ hiddenSuggestionKinds: kinds });
  const hideKind = (k: SuggestionKind) => {
    const before = hiddenKinds;
    setHiddenKinds([...new Set([...hiddenKinds, k])]);
    toast(`You won't be suggested ${SUGGESTION_RULES[k].label.toLowerCase()} again, in any tree.`, 'info', { label: 'Undo', run: () => setHiddenKinds(before) });
  };

  return (
    <section className="report-section">
      <h2 className="section-heading">Suggested</h2>
      <p className="muted small-text" style={{ margin: '-4px 0 0' }}>
        What to add next{home ? `, starting closest to ${displayName(home)}` : ''}. Answer or do one, dismiss it, or use its ⋯ menu to add it to your to-dos or stop suggesting that kind.
      </p>

      {counts.size > 1 && (
        <div className="toggle-chips" style={{ flex: 'none' }} role="group" aria-label="Kinds of suggestions">
          <button className="toggle-chip" aria-pressed={!active} onClick={() => setKind(null)}>
            All <span className="count">{open.length}</span>
          </button>
          {(Object.keys(SUGGESTION_RULES) as SuggestionKind[])
            .filter((k) => counts.get(k))
            .map((k) => {
              const Icon = ICONS[k];
              return (
                <button key={k} className="toggle-chip" aria-pressed={active === k} onClick={() => setKind(active === k ? null : k)}>
                  <Icon size={13} aria-hidden />
                  {SUGGESTION_RULES[k].label} <span className="count">{counts.get(k)}</span>
                </button>
              );
            })}
        </div>
      )}

      <div className="card-box suggestions">
        {list.length ? (
          <ul className="suggestion-list">
            {list.slice(0, limit).map((x) => (
              <SuggestionRow key={x.id} x={x} tree={tree} onDismiss={() => dismiss(x)} onHideKind={() => hideKind(x.kind)} />
            ))}
          </ul>
        ) : (
          <div className="suggestions-done">
            <span className="suggestions-icon" aria-hidden>
              <Sparkles size={17} />
            </span>
            <div>
              <div className="suggestion-title">{person ? `Nothing to suggest about ${displayName(tree.people[person])}` : 'Nothing to suggest right now'}</div>
              <div className="muted small-text">
                {all.length ? 'Everything else is done, on your to-do list, or dismissed. New suggestions appear as the tree grows.' : 'The tree has the details suggestions look for. New ones appear as it grows.'}
              </div>
            </div>
          </div>
        )}
        {list.length > limit && (
          <div className="suggestions-foot">
            <button className="btn small ghost" onClick={() => setLimit((n) => n + PAGE)}>
              Show {Math.min(PAGE, list.length - limit)} more of {list.length - limit}
            </button>
          </div>
        )}
      </div>

      {(gone.length > 0 || hiddenKinds.length > 0) && (
        <details className="card-box dismissed-box">
          <summary>
            <span className="report-title">
              {[gone.length ? `${gone.length} dismissed` : '', hiddenKinds.length ? `${hiddenKinds.length} ${hiddenKinds.length === 1 ? 'kind' : 'kinds'} turned off` : '']
                .filter(Boolean)
                .join(', ')
                .replace(/^./, (c) => c.toUpperCase())}
            </span>
            {gone.length > 0 && (
              <button
                className="btn small ghost"
                onClick={(e) => {
                  e.preventDefault();
                  restore(gone.map((x) => x.id));
                }}
              >
                <RotateCcw size={14} /> Bring all back
              </button>
            )}
          </summary>
          <ul className="dismissed-list">
            {hiddenKinds
              .filter((k) => SUGGESTION_RULES[k])
              .map((k) => {
                const Icon = ICONS[k];
                return (
                  <li key={`kind:${k}`}>
                    <Icon size={14} aria-hidden className="faint" />
                    <span>
                      <b>Every {SUGGESTION_RULES[k].label.toLowerCase()} suggestion</b>, in all trees
                    </span>
                    <button className="btn small ghost" onClick={() => setHiddenKinds(hiddenKinds.filter((x) => x !== k))}>
                      Show again
                    </button>
                  </li>
                );
              })}
            {gone.map((x) => {
              const Icon = ICONS[x.kind];
              return (
                <li key={x.id}>
                  <Icon size={14} aria-hidden className="faint" />
                  <span>{x.title}</span>
                  <button className="btn small ghost" onClick={() => restore([x.id])}>
                    Bring back
                  </button>
                </li>
              );
            })}
          </ul>
        </details>
      )}

      <HowChosen />
    </section>
  );
}

function SuggestionRow({ x, tree, onDismiss, onHideKind }: { x: Suggestion; tree: Tree; onDismiss: () => void; onHideKind: () => void }) {
  const Icon = ICONS[x.kind];
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const choices = x.choices ?? [{ label: x.actionLabel, action: x.action }];
  // A yes-or-no question gets two equal answers; anything else leads with its main action.
  const question = choices[0].label === 'Yes';
  return (
    <li className="suggestion">
      <span className="suggestion-kind" aria-hidden>
        <Icon size={16} />
      </span>
      <div className="suggestion-text">
        <div className="suggestion-title">{x.title}</div>
        <div className="muted small-text">{x.detail}</div>
        {x.people.length > 0 && (
          <span className="fact-people">
            {x.people.map((id) => (
              <PersonLink key={id} id={id} />
            ))}
          </span>
        )}
      </div>
      <div className="suggestion-actions">
        <div className="suggestion-choices" role="group" aria-label={x.choices ? 'Answer' : undefined}>
          {choices.map((c, i) => (
            <button key={c.label} className={`btn small${i === 0 && !question ? ' primary' : ''}`} onClick={() => (c.action.kind === 'dismiss' ? onDismiss() : act(c.action))}>
              {c.label}
            </button>
          ))}
        </div>
        <button className="btn icon small ghost" onClick={onDismiss} title="Dismiss" aria-label={`Dismiss: ${x.title}`}>
          <X size={15} />
        </button>
        <button
          className="btn icon small ghost"
          title="More"
          aria-label={`More for: ${x.title}`}
          aria-haspopup="menu"
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            setMenu({ x: r.right - 240, y: r.bottom + 4 });
          }}
        >
          <MoreHorizontal size={15} />
        </button>
        {menu && (
          <Menu
            ariaLabel="Suggestion"
            x={menu.x}
            y={menu.y}
            minWidth={240}
            onClose={() => setMenu(null)}
            items={[
              { key: 'todo', label: 'Add to to-dos', icon: <ListPlus size={15} />, onSelect: () => toTodo(x, tree) },
              { key: 'dismiss', label: 'Dismiss this one', icon: <X size={15} />, onSelect: onDismiss },
              'separator',
              { key: 'kind', label: `Never suggest ${SUGGESTION_RULES[x.kind].label.toLowerCase()}`, icon: <EyeOff size={15} />, onSelect: onHideKind },
            ]}
          />
        )}
      </div>
    </li>
  );
}

/** Keep a suggestion on the to-do list, to come back to. It's hidden from suggestions while the to-do exists. */
function toTodo(x: Suggestion, tree: Tree) {
  const id = newId('td');
  commit(
    upsertTodo(tree, {
      id,
      title: x.title,
      notes: x.detail,
      status: 'open',
      priority: 'normal',
      kind: TODO_KINDS[x.kind] ?? 'Further research',
      people: x.people.filter((p) => tree.people[p]),
      createdAt: new Date().toISOString(),
      suggestionId: x.id,
    }),
  );
  toast('Added to your to-dos.', 'success', { label: 'Undo', run: () => (getState().tree?.todos[id] ? commit(removeTodo(getState().tree!, id)) : undo()) });
}

function act(a: SuggestionAction) {
  const tree = getState().tree!;
  const name = (id: Id) => displayName(tree.people[id]);
  switch (a.kind) {
    case 'dismiss':
      return;
    case 'add-relative':
      return openDialog({ type: 'add-relative', personId: a.personId, kind: a.relation, existing: a.existing });
    case 'edit':
      return openDialog({ type: 'edit-person', personId: a.personId, field: a.field });
    case 'set-living': {
      const p = tree.people[a.personId];
      commit(upsertPerson(tree, { ...p, living: a.living }));
      return a.living
        ? toast(`Recorded ${name(a.personId)} as living.`, 'success', { label: 'Undo', run: undo })
        : toast(`Recorded ${name(a.personId)} as deceased.`, 'success', { label: 'Add death date', run: () => openDialog({ type: 'edit-person', personId: a.personId, field: 'death-date' }) });
    }
    case 'set-birth': {
      commit(upsertPerson(tree, withEventField(tree.people[a.personId], 'birth', 'date', a.date)));
      return toast(`Recorded ${name(a.personId)}'s birth as ${formatDate(a.date)}.`, 'success', { label: 'Undo', run: undo });
    }
    case 'set-gender': {
      const p = tree.people[a.personId];
      // Pronouns follow, as in the editor: she/her for a woman, he/him for a man, when none are recorded.
      commit(upsertPerson(tree, { ...p, gender: { identity: a.identity }, pronouns: p.pronouns?.trim() ? p.pronouns : defaultPronouns(a.identity) }));
      return toast(`Recorded ${name(a.personId)}'s gender.`, 'success', { label: 'Undo', run: undo });
    }
    case 'merge':
      return openDialog({ type: 'merge', personId: a.personId, otherId: a.otherId });
    case 'edit-relationship':
      return openDialog({ type: 'edit-relationship', relId: a.relId });
    case 'cite':
      return openDialog({ type: 'edit-citation', personId: a.personId, target: { personId: a.personId, eventId: a.eventId } });
    case 'view':
      return setView(a.view);
    case 'search':
      setState({ peopleQuery: a.query });
      return setView('people');
    case 'set-home':
      commit({ ...tree, homePersonId: a.personId });
      return toast(`${name(a.personId)} is now the home person.`, 'success');
    case 'link-partners': {
      const r = addRelationship(tree, { kind: 'partner', a: a.personId, b: a.otherId, type: 'partner', status: 'current' });
      commit(r.tree);
      return toast(`Recorded ${name(a.personId)} and ${name(a.otherId)} as partners.`, 'success', { label: 'Say what kind', run: () => openDialog({ type: 'edit-relationship', relId: r.rel.id }) });
    }
    case 'link-parents': {
      let t = tree;
      const added: Relationship[] = [];
      for (const p of a.parentIds) {
        if (wouldCreateCycle(t, p, a.personId)) continue;
        const r = addRelationship(t, { kind: 'parent', a: p, b: a.personId, type: 'biological' });
        t = r.tree;
        added.push(r.rel);
      }
      if (!added.length) return toast('That would make someone their own ancestor, so nothing was linked.', 'error');
      commit(t);
      return toast(`Linked ${added.map((r) => name(r.a)).join(' and ')} as ${name(a.personId)}'s biological ${added.length === 1 ? 'parent' : 'parents'}.`, 'success', {
        label: 'Change kind',
        run: () => openDialog({ type: 'edit-relationship', relId: added[0].id }),
      });
    }
  }
}

/** How suggestions are found and put in order, from the same numbers the ordering uses. Collapsed until asked for. */
function HowChosen() {
  const kinds = (Object.keys(SUGGESTION_RULES) as SuggestionKind[]).sort((a, b) => SUGGESTION_RULES[b].base - SUGGESTION_RULES[a].base);
  const closeness = [
    { what: 'The home person', w: CLOSENESS[0] },
    { what: 'One link away (a parent, child, partner, sibling, or friend)', w: CLOSENESS[1] },
    { what: 'Two links away', w: CLOSENESS[2] },
    { what: 'Three links away', w: CLOSENESS[3] },
    { what: 'Four or more links away', w: CLOSENESS_FAR },
    { what: 'Not linked to the home person at all', w: CLOSENESS_UNLINKED },
    { what: 'Anyone, when there’s no home person', w: CLOSENESS_NO_HOME },
  ];
  return (
    <details className="card-box relate-ranking">
      <summary>How suggestions are chosen</summary>
      <div className="stack small-text" style={{ marginTop: 10 }}>
        <p>
          Suggestions look for gaps and contradictions in the tree: people without parents, dates, or names; records that disagree; people who are probably the same person or probably
          related in a way that isn’t recorded. Each kind has a base score for how much it usually matters. A score is then multiplied by how close its person is to the home person, so
          the list starts with your own family and works outward.
        </p>
        <table className="rank-table">
          <thead>
            <tr>
              <th scope="col">Base</th>
              <th scope="col">Suggestion</th>
              <th scope="col">When it’s made</th>
            </tr>
          </thead>
          <tbody>
            {kinds.map((k) => (
              <tr key={k}>
                <td>{SUGGESTION_RULES[k].base}</td>
                <td>{SUGGESTION_RULES[k].label}</td>
                <td>{SUGGESTION_RULES[k].when}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table className="rank-table">
          <thead>
            <tr>
              <th scope="col">Times</th>
              <th scope="col">Closeness to the home person</th>
            </tr>
          </thead>
          <tbody>
            {closeness.map((c) => (
              <tr key={c.what}>
                <td>×{c.w}</td>
                <td>{c.what}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p>
          <b>Parents</b> count ×{DIRECT_LINE} more for the home person’s direct line (their parents, grandparents, and so on back), and ×{ELDER} more for a living person aged 60 or over,
          who may remember names and places no record has. A suggestion about two people counts as close as the closer of them. Kinds limited to people within two or three links
          apply to everyone when there’s no home person to measure from.
        </p>
        <p>
          <b>Order.</b> Highest score first, except that each further suggestion of a kind already listed counts ×{VARIETY} of the one before it, so the top of the list mixes kinds
          instead of being ten “add parents” in a row.
        </p>
        <p>
          <b>What’s left out.</b> Suggestions you dismiss stay hidden on this device until you bring them back, and a kind you choose never to see (from a suggestion’s ⋯ menu)
          stays hidden in every tree. One added to your to-dos is hidden while that to-do exists. And a suggestion goes away by itself once the tree has what it asked for.
        </p>
      </div>
    </details>
  );
}
